
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ThreeAssistant from "@/components/assistant/ThreeAssistant";
import * as Sentry from "@sentry/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";

// Wrap with Sentry profiler
const AssistantTestPage = () => {
  const [isResponding, setIsResponding] = useState(false);
  const [conversation, setConversation] = useState<Array<{type: 'user' | 'assistant', text: string}>>([]);
  const [question, setQuestion] = useState("");
  const [assistantType, setAssistantType] = useState("futuristicBall");

  const handleAskQuestion = (question: string) => {
    if (!question.trim()) return;
    
    // Add user question to conversation
    setConversation(prev => [...prev, {type: 'user', text: question}]);
    
    // Simulate assistant response
    setIsResponding(true);
    
    // Add Sentry breadcrumb
    Sentry.addBreadcrumb({
      category: 'assistant',
      message: `User asked: ${question}`,
      level: 'info',
    });

    // Simulate a delay for the response
    setTimeout(() => {
      const response = `I received your question: "${question}". This is a simulated response from the 3D assistant.`;
      setConversation(prev => [...prev, {type: 'assistant', text: response}]);
      setIsResponding(false);
    }, 2000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleAskQuestion(question);
    setQuestion("");
  };

  const handleAssistantChange = (value: string) => {
    setAssistantType(value);
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8 text-center">AI Assistant</h1>
      
      <div className="grid grid-cols-1 gap-6">
        {/* Assistant style selector - positioned at the top */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Assistant Appearance</CardTitle>
          </CardHeader>
          <CardContent>
            <ThreeAssistant 
              isResponding={isResponding}
              selectorOnly={true}
              onAssistantChange={handleAssistantChange}
              assistantType={assistantType}
            />
          </CardContent>
        </Card>
        
        {/* Main conversation area - now full width at top */}
        <Card className="w-full">
          <CardContent className="p-4 h-[400px] overflow-y-auto bg-muted/20">
            {conversation.length === 0 ? (
              <div className="h-full flex items-center justify-center text-center text-muted-foreground italic">
                Start a conversation with the AI assistant
              </div>
            ) : (
              <div className="space-y-4">
                {conversation.map((msg, index) => (
                  <div 
                    key={index} 
                    className={`p-3 rounded-lg max-w-[80%] ${
                      msg.type === 'user' 
                        ? 'bg-primary text-primary-foreground ml-auto' 
                        : 'bg-muted/80 text-foreground'
                    }`}
                  >
                    {msg.text}
                  </div>
                ))}
                {isResponding && (
                  <div className="bg-muted/80 text-foreground p-3 rounded-lg max-w-[80%] animate-pulse">
                    Thinking...
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Input section with improved layout */}
        <div className="flex items-center bg-card rounded-lg shadow-sm p-2">
          <div className="flex-shrink-0 mr-2" style={{ width: '70px', height: '70px' }}>
            <ThreeAssistant 
              isResponding={isResponding}
              hideSelector={true}
              hideForm={true}
              assistantType={assistantType}
              containerSize={70}
              hideRespondingLabel={true}
            />
          </div>
          
          <form onSubmit={handleSubmit} className="flex flex-1 items-center gap-2">
            <Input
              type="text"
              placeholder="Ask the assistant anything..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="flex-1"
            />
            
            <Button 
              type="submit" 
              disabled={isResponding || !question.trim()}
              size="icon"
              className="flex-shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Sentry.withProfiler(AssistantTestPage);
